/**
 * What a counter reports about ITSELF on every beat
 * (docs/plans/handheld-fleet.html 9).
 *
 * The heartbeat used to carry four booleans that are true of a browser and a
 * version string, and a fleet is managed on rather more than that. This is the
 * one optional object the Sunmi fills; the browser tablet sends none, and
 * nothing here is inferred, typed by a human, or personal - every field is a
 * synchronous system call the device already has an answer for.
 *
 * ## Why it lives in its own file
 *
 * Three mounts read it and none of them owns it. The device WRITES it on
 * `POST /b/heartbeat`; the per-shop pairing dialog and the platform-wide fleet
 * list both READ it back on a device row, on `/internal` and on `/c`. A shape
 * declared beside any one of those would be restated beside the next, and the
 * copies would then disagree about a record whose whole job is to be one
 * device's current state.
 *
 * ## Current state, never a series
 *
 * It is OVERWRITTEN every beat, on the device row, in one `jsonb` column. A
 * fleet screen wants now; a history of battery percentages is a time series
 * this database is not for. The facts worth keeping over time - a version
 * change, a reboot, an install outcome - become audit rows instead
 * (`device.version_changed`, `device.rebooted`, `device.update_installed`,
 * `device.update_failed`), so a device's timeline is the audit log filtered to
 * one subject.
 *
 * ## Nothing personal, and the never-table says so
 *
 * No location, no contacts, no photos, no typed text - the app has none of
 * these to report and reporting any would be the first (plan 18). The serial is
 * the DEVICE's, which is what a founder reads off the box when a unit goes back
 * for warranty.
 */

import { z } from 'zod';
import { Instant } from './common';

/**
 * Which transport the counter is on. `NONE` is a device that beat through a
 * queue drained a moment later, or one whose only interface just dropped - it
 * is reported rather than inferred, because "no network" and "the field is
 * missing" are different facts.
 */
export const HandheldNetworkKind = z.enum(['WIFI', 'CELLULAR', 'ETHERNET', 'NONE']);
export type HandheldNetworkKind = z.infer<typeof HandheldNetworkKind>;

/**
 * What the counter was connected to at one instant.
 *
 * Named once because two shapes report it and they must not drift: the beat's
 * telemetry, where it is the device's CURRENT transport, and every failed
 * request below, where it is the transport that request died on. Half of a
 * counter's failures are answered by that one field - a `SocketTimeoutException`
 * on `CELLULAR` at one bar is a shop with no wifi, and the same exception on
 * `WIFI` at four bars is the platform.
 */
export const HandheldNetworkState = z.object({
  kind: HandheldNetworkKind,
  /** 0-4 as Android reports it; null when the transport has no notion of it. */
  signal: z.int().min(0).max(4).nullable(),
});
export type HandheldNetworkState = z.infer<typeof HandheldNetworkState>;

/**
 * How the last staged build ended (plan 7, 12).
 *
 * Four of the five are failures and each names a different fix: the installer
 * refused (the baker declined, or Unknown sources is off), the bytes did not
 * hash (a truncated or rewritten download), the archive was signed by the wrong
 * key (the one case that must die quietly on the device), or the download never
 * finished at all.
 */
export const HandheldInstallOutcome = z.enum([
  'INSTALLED',
  'REFUSED_BY_INSTALLER',
  'HASH_MISMATCH',
  'SIGNATURE_MISMATCH',
  'DOWNLOAD_FAILED',
]);
export type HandheldInstallOutcome = z.infer<typeof HandheldInstallOutcome>;

/**
 * `SunmiPrinterService.updatePrinterState()`, verbatim.
 *
 * Named here rather than left as bare integers at the four places that judge
 * them: 1 normal, 2 preparing, 3 communication error, 4 out of paper, 5 too
 * hot, 505 the printer service is absent (which on this hardware means the
 * vendor service did not bind, not that the printer was unplugged).
 *
 * Kept as an INT on the wire, not an enum: it is the vendor's number and a ROM
 * that invents a sixth must reach the fleet list as itself rather than fail the
 * beat that carried it.
 */
export const PRINTER_STATE = {
  NORMAL: 1,
  PREPARING: 2,
  COMMS_ERROR: 3,
  OUT_OF_PAPER: 4,
  OVERHEATED: 5,
  ABSENT: 505,
} as const;

/**
 * Every alert the counter fleet can raise about a device that is TALKING
 * (plan 10).
 *
 * Here, in `contracts`, rather than beside the rules that raise them in the
 * worker - and the reason is the catalogue. Both `activity.alert.<KIND>` titles
 * are owed by `apps/internal-admin`, which cannot import `apps/api-worker` and
 * never will; a list that lived there could only be asserted by the app that
 * writes it, which is the assertion that proves nothing. Stated on the wire
 * package both sides already depend on, the completeness test walks it, and a
 * tenth kind added without copy is a build failure rather than
 * `DEVICE_STORAGE_LOW` printed into a table cell.
 *
 * `TABLET_HEARTBEAT_MISSING` is deliberately NOT here. It remains the only
 * alert about ABSENCE (OPS-011); every kind below is about a device that is
 * talking and saying something wrong. The fix for one is a phone call and the
 * fix for the other is a visit, and confusing them costs a journey.
 */
export const HANDHELD_ALERT_KINDS = [
  'PRINTER_OUT_OF_PAPER',
  'PRINTER_FAULT',
  'DEVICE_BATTERY_LOW',
  'DEVICE_CLOCK_SKEW',
  'DEVICE_STORAGE_LOW',
  'DEVICE_UPDATE_FAILED',
  'DEVICE_CRASH_LOOP',
  'DEVICE_PAIRED',
  'DEVICE_REBOOTED',
] as const;
export type HandheldAlertKind = (typeof HANDHELD_ALERT_KINDS)[number];

/**
 * Every audit action the counter fleet writes about a DEVICE, here for the same
 * reason as the kinds above: `activity.audit.<action>` is copy the operations
 * portal owes, and this is the only package both it and the worker can see.
 *
 * The first four are telemetry CHANGES (plan 9). The record itself is current
 * state and is overwritten every beat; these are the facts worth keeping, so a
 * device's timeline is the audit log filtered to one subject.
 *
 * The next three are the desk acting on a counter (plan 11) - a command issued,
 * the device's answer to it, and a configuration layer moved. `handheld.config_updated`
 * is written about all THREE layers under their own subject types (the
 * platform, a shop, a device), so it appears once here and carries one title.
 *
 * The last two are what a counter LEFT BEHIND (plan 13): a crash it reported on
 * its next launch, and a diagnostic bundle an operator asked for. Both carry
 * the KEY of an object in the FLEET bucket and never its bytes - the same
 * identity-not-address rule every image on this platform obeys.
 */
export const HANDHELD_DEVICE_AUDIT_ACTIONS = [
  'device.version_changed',
  'device.rebooted',
  'device.update_installed',
  'device.update_failed',
  'device.command_issued',
  'device.command_acknowledged',
  'handheld.config_updated',
  'device.crashed',
  'device.diagnostics_uploaded',
  /*
   * The desk renaming a counter (plan 5). It is the LABEL every screen
   * identifies a device by - the fleet list, the adoption panel's failed rows,
   * the shop's own Devices modal - so "which counter is the one in the alert"
   * changes meaning the moment somebody edits it, and the row that says who
   * did and what it used to be called is the only way back.
   */
  'device.renamed',
  /*
   * The desk retiring a handheld from the fleet (founder, 2026-09-15). Leaving
   * a shop no longer ends a device's life on the platform; this does, and it is
   * the one act that stops a handheld's builds - so who did it, and to which
   * machine, is a row the timeline has to keep.
   */
  'device.retired',
  /*
   * A request this counter made that did not work (`HandheldRequestFailure`).
   *
   * The same reasoning as the four telemetry changes above it, one step further
   * out: the beat's record is what the device IS and is overwritten every thirty
   * seconds, so a request that failed a minute ago is already gone from it. The
   * FAILURE is the fact worth keeping - it is the only thing the desk has when a
   * baker says the counter would not send an order - and a device's timeline is
   * the audit log filtered to one subject.
   */
  'device.request_failed',
] as const;
export type HandheldDeviceAuditAction = (typeof HANDHELD_DEVICE_AUDIT_ACTIONS)[number];

/**
 * A counter AVD rather than a machine on a counter, read off `Build.MODEL`.
 *
 * A Sunmi says `V2`; every Android emulator image says "Android SDK built for
 * <abi>" or `sdk_gphone...`. Stated ONCE here because two readers want it and
 * must agree: pairing, which numbers an emulator in its own series (`Sunmi
 * E001`), and the fleet list, which prints "emulator" under the kind. Never
 * read off the label - a label is whatever was typed, and the demo fleet held
 * an emulator named "Sunmi 00005" between two real units (2026-09-18).
 */
export function isEmulatorModel(model: string | null | undefined): boolean {
  return typeof model === 'string' && /^(android sdk built for|sdk_)/i.test(model.trim());
}

export const HandheldTelemetry = z.object({
  /** `BuildConfig.VERSION_CODE` - the integer Android enforces; `appVersion` stays the string. */
  appVersionCode: z.int().positive().max(2_100_000_000),
  /** `Build.MODEL`, `Build.DISPLAY` (the ROM build id), `Build.VERSION.SECURITY_PATCH`. */
  model: z.string().max(60),
  osBuild: z.string().max(80),
  /** Null on 7.1, which predates the field - the fleet this app lives on. */
  securityPatch: z.string().max(20).nullable(),
  /**
   * `Build.SERIAL` - permitted without a runtime permission below API 26. A
   * fleet column and NOT an identity: on the Sunmi V2 it is a MODEL-level
   * constant (`VB63229R20220` on every unit, measured 2026-08-28), so it cannot
   * identify a unit and the app-minted device id is the identity.
   */
  serial: z.string().max(60).nullable(),
  /**
   * `Settings.Secure.ANDROID_ID`, which is what a Sunmi presents as its device
   * id from build 717 on (`DeviceIdentity`). Reported on the beat so the fleet
   * can answer "is it unique across our units" from a screen rather than a
   * cable - the question the serial failed. OPTIONAL: a counter on an older
   * build goes on beating without it.
   */
  androidId: z.string().max(64).nullable().optional(),
  /**
   * Whether Android considers the counter application this device's DEVICE
   * OWNER, which is the one thing that lets a verified release install without
   * a tap (BAK-056, `Owner.java`).
   *
   * OPTIONAL, and it stays optional: a counter installed before the field
   * shipped goes on beating without it, and a browser tablet has no notion of
   * it at all. Absent therefore means "not known to be", never "no" - which is
   * why the device page prints one word for true and a dash for everything
   * else rather than a Yes/No pair that would state a fact nobody measured.
   */
  deviceOwner: z.boolean().optional(),
  /**
   * Whether Android's Doze exempts the counter application
   * (`PowerManager.isIgnoringBatteryOptimizations`, `Vigil.dozeExempt`). Not
   * exempt, a dark counter off its charger goes into deep idle and its wake-up
   * fires once every nine minutes, so a paid order rings up to nine minutes
   * late. Only a cable install puts a device on that list
   * (`scripts/install-handheld.mjs`), so this is what says which units still
   * need a visit.
   *
   * OPTIONAL, like `deviceOwner`: a counter on a build older than the field
   * says nothing, and nothing is "not known to be", never "no".
   */
  dozeExempt: z.boolean().optional(),
  battery: z.object({ percent: z.int().min(0).max(100), charging: z.boolean() }),
  network: HandheldNetworkState,
  storageFreeMb: z.int().nonnegative(),
  /** `SystemClock.elapsedRealtime()` at the beat - a reboot resets it, which is the point. */
  uptimeSeconds: z.int().nonnegative(),
  /** Device clock minus platform clock, from the offset the session already keeps. */
  clockSkewMillis: z.int(),
  /** See `PRINTER_STATE`; null when the device has not asked yet. */
  printerState: z.int().nullable(),
  /** The build it was asked to install, if any, and how the last attempt ended. */
  update: z.object({
    stagedVersionCode: z.int().positive().max(2_100_000_000).nullable(),
    lastInstallOutcome: HandheldInstallOutcome.nullable(),
    /**
     * WHICH BUILD that outcome is about - an outcome with no version says
     * nothing (`Session.updateOutcomeVersionCode`, whose javadoc has said so
     * since the field existed).
     *
     * OPTIONAL, and it stays optional: a counter installed before this field
     * shipped goes on beating without it, and the rule below falls back to the
     * old reading for one rather than calling its outcome stale on a guess.
     */
    outcomeVersionCode: z.int().positive().max(2_100_000_000).nullable().optional(),
  }),
  /** Uncaught exceptions since the last beat the platform acknowledged. */
  crashesSinceLastAck: z.int().nonnegative(),
  /** The config version the device is currently applying (plan 11). */
  configVersion: z.int().nonnegative(),
});
export type HandheldTelemetry = z.infer<typeof HandheldTelemetry>;

/**
 * One request the counter made that did not work.
 *
 * A counter showed "Pas de connexion" while a baker pressed "Commande prete"
 * with a courier waiting, and the platform held NOTHING about it: the beat
 * carries what the device IS, never what it just failed to do, and the app's own
 * log ring leaves the device only on an operator's `UPLOAD_DIAGNOSTICS` - which
 * is a command somebody has to think to send, hours later, to a device that has
 * since rebooted. So a failure rides the next beat instead, and the desk reads
 * it on the device's timeline in a minute.
 *
 * The failure is the whole record: nothing here is a request or a response BODY,
 * and nothing here is personal. A `/b` path names an order and never a guest
 * (plan 18's never-table), and `detail` is an exception's class and message or
 * the platform's own - never what was being sent when it failed.
 */
export const HandheldRequestFailure = z.object({
  /** The DEVICE's instant when the request ended. */
  at: Instant,
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  /** Path only, no host, no query: `/b/orders/<uuid>/ready`. Order ids are fine; nothing personal is ever in a /b path. */
  path: z.string().min(1).max(160),
  /** HTTP status read, or 0 when none was (transport failure). */
  status: z.int().min(0).max(599),
  /** The platform error code when a status was read (`conflict`, `not_found`...), or the client's own class of failure when none was: `network_error`, `invalid_response`, `method_unsupported`. */
  code: z.string().min(1).max(40),
  /** Exception class and message for a transport failure (`java.net.SocketTimeoutException: Read timed out`), or the platform's message. Never a request or response body. */
  detail: z.string().max(240).nullable(),
  durationMs: z.int().nonnegative().max(600_000),
  /** What the device was connected to at that instant - same shape as telemetry.network. */
  network: HandheldNetworkState,
});
export type HandheldRequestFailure = z.infer<typeof HandheldRequestFailure>;

/**
 * How many failures one beat may carry.
 *
 * A cap and not a page, for the same reason the command queue has one: a counter
 * that has lost its network fails every request it makes, twice a minute, and a
 * beat that carried the whole backlog would be a device turning its own outage
 * into a flood of audit rows. Ten is more than an incident needs to be
 * recognizable - the eleventh says the same thing as the tenth - and the device
 * drops the rest rather than growing a queue it would then have to drain.
 */
export const HANDHELD_FAILURES_PER_BEAT = 10;

/**
 * The install outcome that is still TRUE of the build this counter is RUNNING.
 *
 * `lastInstallOutcome` is current state that nothing ever clears, so a counter
 * that failed an install and then got the build by some other route - a hand
 * install, a warranty swap, a second offer that worked - went on reporting the
 * failure for ever. Read literally, that put a counter running 694 under
 * "FAILED INSTALLS" for 694 on the adoption panel while its own row, a few
 * pixels away, printed 694 as its version (measured on the emulator charter,
 * 2026-08-27). Two answers on one screen, which is this codebase's
 * second-largest defect family and always looks exactly like this.
 *
 * Stated ONCE here, over the wire type, because three readers want it: the
 * adoption panel that buckets a device by build, the device page that prints
 * "Last install", and the counter itself, which stops reporting an outcome its
 * own version code has overtaken. `INSTALLED` is never stale - it is a fact
 * about the build now running and reads correctly beside it.
 *
 * A device that sends no `outcomeVersionCode` gets the old reading, which is
 * the only backward-compatible answer: "no version" cannot distinguish an
 * outcome about this build from one about the next.
 */
export function liveInstallOutcomeOf(
  telemetry: Pick<HandheldTelemetry, 'appVersionCode' | 'update'>,
): HandheldInstallOutcome | null {
  const outcome = telemetry.update.lastInstallOutcome;
  if (outcome === null || outcome === 'INSTALLED') return outcome;
  const about = telemetry.update.outcomeVersionCode;
  if (about === null || about === undefined) return outcome;
  return telemetry.appVersionCode >= about ? null : outcome;
}
